import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as _ from "lodash";
import { Table } from "primeng/table";
import { first, takeUntil } from "rxjs/operators";
import { ColumnTypes } from "src/app/shared/Models/enums/ColumnTypeEnum";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { SnsRecordStatus } from "src/app/shared/Models/enums/SnsRecordStatusEnum";
import { DataConvertingService } from "src/app/shared/services/data-converting.service";
import { DateFormattingService } from "src/app/shared/services/date-formatting.service";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { FilterableTableComponent } from "src/app/shared/table/filterable-table.component";
import { ToastService } from "src/app/shared/toast.service";
import { BuildingTypeEnum } from "../../../shared/Models/enums/BuildingTypeEnum";
import { TSAType } from "../../../shared/Models/enums/TSATypeEnum";
import { SurveyStatus } from "../../../shared/Models/enums/SurveySurveyStatus";
import {
  Figures,
  RandomFigureGeneratorService,
} from "../../../shared/services/figure.service";
import { BuildingGroupService } from "../../building-list/services/building-group.service";
import { Quadrant } from "../../survey/inside-survey/models/quadrant.enum";
import { DeviceCheckService } from "../../survey/shared/services/device-check.service";
import { DocumentListItemDTO } from "../models/documentListItemsDTO";
import { DocumentState } from "../models/documentState";
import { UploadBuildingDocumentPhoto } from "../models/uploadBuildingDocumentPhoto";
import { DocumentListService } from "../services/documentList.service";
import {
  DocumentGeneralRemarkModalComponent,
  DocumentGeneralRemarkModalSaveResponse,
} from "./document-general-remark-modal/document-general-remark-modal.component";
import {
  DocumentListValidationItem,
  DocumentPrintResponse,
} from "./document-preview/document-preview.component";
import {
  DocumentRejectionModalComponent,
  DocumentRejectionResponse,
} from "./document-rejection-modal/document-rejection-modal.component";
import { SignedDocumentApprovalResponse } from "./signed-document-approval/signed-document-approval.component";
import {
  SignedDocumentArchiveModalComponent,
  SignedDocumentArchiveResponse,
} from "./signed-document-archive/signed-document-archive-modal.component";
import { TemplateLanguage } from "src/app/shared/Models/enums/TemplateLanguage.enum";
import { TemplateType } from "src/app/shared/Models/enums/TemplateType.enum";
import { Area } from "src/app/shared/Models/read";

enum State {
  List,
  Validation,
  Approval,
  SignedPreview,
  RejectedPreview,
}

interface DocTypeViewModel {
  type: TemplateType;
  header: string;
}

interface LanguageViewModel {
  language: TemplateLanguage;
  label: string;
  class: string;
}

@Component({
  selector: "app-document-list",
  templateUrl: "./document-list.component.html",
  styleUrls: ["./document-list.component.scss"],
})
export class DocumentListComponent
  extends FilterableTableComponent<DocumentListItemDTO>
  implements OnInit
{
  @ViewChild("dt") dataTable: Table;
  @ViewChildren("uploadInput, uploadInput1")
  uploadInputs: QueryList<ElementRef<HTMLInputElement>>;

  surveyStatus = SurveyStatus;
  tsaType = TSAType;
  buildingType = BuildingTypeEnum;
  selectedValidationObject: DocumentListValidationItem;
  selectedApprovalObject: DocumentListItemDTO;
  quadrant = Quadrant;
  snsRecordStatus = SnsRecordStatus;
  state: State;
  canWrite: boolean;
  areaId: string;
  isDefaultOwner: boolean;

  scrollOffsetLandscape: number = 105;
  scrollOffsetPortrait: number = 105;

  docType = TemplateType;

  docTypes: DocTypeViewModel[] = [
    { type: TemplateType.TSA, header: "TSA" },
    { type: TemplateType.SSR, header: "SSR" },
  ];
  docLanguages: LanguageViewModel[] = [
    { language: TemplateLanguage.nl, label: "NL", class: "nlChip" },
    { language: TemplateLanguage.fr, label: "FR", class: "frChip" },
    { language: TemplateLanguage.de, label: "DE", class: "deChip" },
  ];

  allCols = [
    {
      field: "buildingGroupId",
      header: $localize`:@@general.buildinggroup.short:BG`,
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "buildingGroup",
      transform: (data: DocumentListItemDTO) => data.figure,
      filterSearch: false,
      hasEmptyValue: true,
    },
    {
      field: "buildingLomKey",
      header: "KEYS",
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
      ignoredFilterValues: [0],
    },
    {
      field: "address",
      header: $localize`:@@general.address:Address`.toUpperCase(),
      visibleOnPortrait: true,
      hasFilter: true,
    },
    {
      field: "number",
      header: "NR",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "surveyInfo",
      header: "SURVEY INFO",
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "tsa",
      header: "TSA",
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "tsaInfo",
      header: "TSA INFO",
      visibleOnPortrait: true,
      hasFilter: false,
      transform: (data: DocumentListItemDTO) =>
        `${this.formatDate(data.tsaSentDate) ?? ""} ${
          this.formatDate(data.expectedFeedback) ?? ""
        }`,
    },
    {
      field: "tsaType",
      header: "TSA TYPE",
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "ssr",
      header: "SSR",
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "snsInfo",
      header: "SNS INFO",
      visibleOnPortrait: true,
      hasFilter: false,
      transform: (data: DocumentListItemDTO) =>
        `${SnsRecordStatus[data.tsaSnsUploadStatus] ?? ""} ${
          this.formatDate(data.tsaSnsUploadTime) ?? ""
        } ${SnsRecordStatus[data.ssrSnsUploadStatus] ?? ""} ${
          this.formatDate(data.ssrSnsUploadTime) ?? ""
        }`,
    },
    {
      field: "remarks",
      header: "REMARKS",
      visibleOnPortrait: true,
      hasFilter: false,
    },
  ];

  @ViewChild("buildingGroup") buildingGroupTemplate: TemplateRef<{
    data: { Figure: Figures; Color: string };
  }>;

  constructor(
    private documentListService: DocumentListService,
    private _figureService: RandomFigureGeneratorService,
    private readonly modalService: NgbModal,
    private readonly dateFormatter: DateFormattingService,
    private readonly dataService: DataConvertingService,
    private readonly buildingGroupService: BuildingGroupService,
    private readonly dialogService: DialogService,
    toastService: ToastService,
    deviceCheckService: DeviceCheckService,
    private permissionsService: PermissionsService
  ) {
    super(toastService, deviceCheckService);
    this.canWrite = this.permissionsService.allowed(
      PermissionsEnum.EditDocumentList
    );
  }

  toggleNoAdminTSA(dto: DocumentListItemDTO) {
    dto.noTSA = false;
    dto.tsa = false;
    if (dto.documentStatus > DocumentState.Pending) {
      this.resetTsaAndChangeType(dto, TSAType.NoAdmin);
    } else {
      var tsaType = TSAType.NoAdmin;
      this.setDocumentType(dto.insideSurveyId, tsaType);
    }
  }

  toggleNoTSA(dto: DocumentListItemDTO) {
    dto.noAdminTSA = false;
    dto.tsa = false;
    if (dto.documentStatus > DocumentState.Pending) {
      this.resetTsaAndChangeType(dto, TSAType.NoTsa);
    } else {
      var tsaType = TSAType.NoTsa;
      this.setDocumentType(dto.insideSurveyId, tsaType);
    }
  }

  toggleTSA(dto: DocumentListItemDTO) {
    dto.noTSA = false;
    dto.noAdminTSA = false;
    if (dto.documentStatus > DocumentState.Pending) {
      this.resetTsaAndChangeType(dto, TSAType.Normal);
    } else {
      var tsaType = TSAType.Normal;
      this.setDocumentType(dto.insideSurveyId, tsaType);
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.state = State.List;
    if (
      sessionStorage.getItem("block") &&
      sessionStorage.getItem("block") != "null"
    ) {
      const block = JSON.parse(sessionStorage.getItem("block"));
      this.onUpdateAreaId(block);
    }
  }

  getStatusCellClass(item: DocumentListItemDTO) {
    return {
      rejected: item.rejected || item.signedButRejected,
      approved: item.approved,
    };
  }

  onUpdateAreaId(selectedBlock: Area) {
    this.tableValues = [];
    this.clearAllFilter();
    this.areaId = selectedBlock.id;

    sessionStorage.setItem("areaId", this.areaId);
    this.getData();
  }

  updateItems(newItems: DocumentListItemDTO[]) {
    this.tableValues = this.tableValues.map((x) => {
      let addressFoundToBeUpdated = newItems.find(
        (item) =>
          item.buildingId === x.buildingId && item.addressId === x.addressId
      );

      if (addressFoundToBeUpdated) {
        (addressFoundToBeUpdated as any).figure = (x as any).figure;

        addressFoundToBeUpdated.tsaSentDate = x.tsaSentDate;
        addressFoundToBeUpdated.expectedFeedback = x.expectedFeedback;
        addressFoundToBeUpdated.approvedDate = x.approvedDate;
        addressFoundToBeUpdated.tsaSnsUploadStatus = x.tsaSnsUploadStatus;
        addressFoundToBeUpdated.ssrSnsUploadStatus = x.ssrSnsUploadStatus;

        return addressFoundToBeUpdated;
      }

      return x;
    });
    this.globalFilter$.next(this.globalFilter);
  }

  openPreviewScreen(
    lang: TemplateLanguage,
    type: TemplateType,
    item: DocumentListItemDTO
  ) {
    this.selectedValidationObject = {
      documentItem: item,
      lang,
      type,
    };
    this.state = State.Validation;
  }

  closeValidationScreen() {
    this.state = State.List;
    this.ngOnInit();
  }

  get validationScreenVisible() {
    return this.state === State.Validation;
  }

  openApprovalScreen(item: DocumentListItemDTO) {
    this.selectedApprovalObject = item;
    this.state = State.Approval;
  }

  closeApprovalScreen() {
    this.state = State.List;
    this.ngOnInit();
  }

  get approvalScreenVisible() {
    return this.state === State.Approval;
  }

  onSignedValidate(item: DocumentListItemDTO) {
    this.documentListService
      .approve(item.buildingId)
      .pipe(first())
      .subscribe(() => {
        this.documentListService
          .getDocumentByBuildingId(item.buildingId)
          .pipe(first())
          .subscribe((newDocs) => {
            this.updateItems(newDocs);
            this.closeApprovalScreen();
          });
      });
  }

  onSignedReject(response: SignedDocumentApprovalResponse) {
    const { item, remarks } = response;
    this.documentListService
      .rejectSigned(item.buildingId, remarks)
      .pipe(first())
      .subscribe(() => {
        this.documentListService
          .getDocumentByBuildingId(item.buildingId)
          .pipe(first())
          .subscribe((newDocs) => {
            this.updateItems(newDocs);
            this.closeApprovalScreen();
          });
      });
  }

  onSignedClose() {
    this.state = State.List;
    this.ngOnInit();
  }

  onGeneralRemarksClick(item: DocumentListItemDTO) {
    const modalRef = this.modalService.open(
      DocumentGeneralRemarkModalComponent,
      { size: "lg" }
    );
    modalRef.componentInstance.remarks = item.generalRemarks;
    modalRef.componentInstance.save
      .pipe(first())
      .subscribe((response: DocumentGeneralRemarkModalSaveResponse) => {
        this.documentListService
          .addGeneralRemarks(item.buildingId, response.remarks)
          .pipe(first())
          .subscribe(() => {
            this.documentListService
              .getDocumentByBuildingId(item.buildingId)
              .pipe(first())
              .subscribe((newDocs) => {
                this.updateItems(newDocs);
                response.callback();
              });
          });
      });
  }

  onDeleteClick(item: DocumentListItemDTO) {
    if (!item.canBeDeleted) return;

    const modalRef = this.modalService.open(
      SignedDocumentArchiveModalComponent,
      { size: "lg" }
    );
    modalRef.componentInstance.remarks = null;
    modalRef.componentInstance.save
      .pipe(first())
      .subscribe((response: SignedDocumentArchiveResponse) => {
        this.documentListService
          .deleteTsa(item.buildingId, response.remarks)
          .pipe(first())
          .subscribe(() => {
            this.documentListService
              .getDocumentByBuildingId(item.buildingId)
              .pipe(first())
              .subscribe((newDocs) => {
                this.updateItems(newDocs);
              });
          });
      });
  }

  tableCellUploadId(item: DocumentListItemDTO) {
    return `table-cell-upload-${item.buildingId}`;
  }

  onUploadClick(item: DocumentListItemDTO) {
    this.uploadInputs
      .find((x) => x.nativeElement.id === this.tableCellUploadId(item))
      .nativeElement.click();
  }

  onUploadFile(e: any, item: DocumentListItemDTO) {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = "";
    this.dataService
      .blobToBase64(file)
      .pipe(first())
      .subscribe((data) => {
        const photoData = this.dataService.parseEmbeddedUrlFromBase64(data);
        this.documentListService
          .uploadSignedDocument(
            new UploadBuildingDocumentPhoto(
              item.buildingId,
              item.buildingId,
              photoData.data,
              photoData.mimetype
            )
          )
          .pipe(first())
          .subscribe(() => {
            this.documentListService
              .getDocumentByBuildingId(item.buildingId)
              .pipe(first())
              .subscribe((newDocs) => {
                this.updateItems(newDocs);
              });
          });
      });
  }

  selectedItemId() {
    return "selected-upload";
  }

  onSelectedUploadClick() {
    this.uploadInputs
      .find((x) => x.nativeElement.id === this.selectedItemId())
      .nativeElement.click();
  }

  async onSelectedUploadFile(e: any) {
    const item = this.selectedApprovalObject;
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = "";
    this.dataService
      .blobToBase64(file)
      .pipe(first())
      .subscribe((data) => {
        const photoData = this.dataService.parseEmbeddedUrlFromBase64(data);
        this.documentListService
          .uploadSignedDocument(
            new UploadBuildingDocumentPhoto(
              item.buildingId,
              item.buildingId,
              photoData.data,
              photoData.mimetype
            )
          )
          .pipe(first())
          .subscribe(() => {
            this.documentListService
              .getDocumentByBuildingId(item.buildingId)
              .pipe(first())
              .subscribe((newDocs) => {
                this.updateItems(newDocs);
                this.state = State.List;
              });
          });
      });
  }

  onBackToPending() {
    const item = this.selectedApprovalObject;
    this.documentListService
      .backToPrinted(item.buildingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.documentListService
          .getDocumentByBuildingId(item.buildingId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((newDocs) => {
            this.updateItems(newDocs);
            this.state = State.List;
          });
      });
  }

  onBackToPendingFull() {
    const item = this.selectedApprovalObject;
    this.documentListService
      .backToPending(item.buildingId)
      .pipe(first())
      .subscribe(() => {
        this.documentListService
          .getDocumentByBuildingId(item.buildingId)
          .pipe(first())
          .subscribe((newDocs) => {
            this.updateItems(newDocs);
            this.state = State.List;
          });
      });
  }

  onOwnerRejectClick(item: DocumentListItemDTO) {
    const modalRef = this.modalService.open(DocumentRejectionModalComponent, {
      size: "lg",
    });
    modalRef.componentInstance.name = _.get(item, "ownerRemarks.name", "");
    modalRef.componentInstance.readonly = false;
    modalRef.componentInstance.remarks = _.get(
      item,
      "ownerRemarks.remarks",
      ""
    );
    modalRef.componentInstance.save
      .pipe(first())
      .subscribe((response: DocumentRejectionResponse) => {
        this.onOwnerReject(item, response);
      });
  }

  onOwnerReject(
    item: DocumentListItemDTO,
    response: DocumentRejectionResponse
  ) {
    this.documentListService
      .reject(item.buildingId, response.name, response.remarks)
      .pipe(first())
      .subscribe(() => {
        this.documentListService
          .getDocumentByBuildingId(item.buildingId)
          .pipe(first())
          .subscribe((newDocs) => {
            this.updateItems(newDocs);
          });
      });
  }

  formatDate(date: Date) {
    if (!date) return;

    return this.dateFormatter.format(date);
  }

  hasRejectionRemarks(item: DocumentListItemDTO) {
    return !!item.ownerRemarks;
  }

  onRejectionRemarksClick(item: DocumentListItemDTO) {
    const modalRef = this.modalService.open(DocumentRejectionModalComponent, {
      size: "lg",
    });
    modalRef.componentInstance.name = _.get(item, "ownerRemarks.name", "");
    modalRef.componentInstance.readonly = true;
    modalRef.componentInstance.remarks = _.get(
      item,
      "ownerRemarks.remarks",
      ""
    );
  }

  async onPrint(item: DocumentPrintResponse) {
    if (
      item.type !== TemplateType.TSA &&
      item.type !== TemplateType.TSANOADMIN
    ) {
      item.callBack();
      return;
    }

    if (item.documentItem.documentStatus !== DocumentState.Pending) {
      item.callBack();
      return;
    }
    let currentBGBuildings = [item.documentItem.buildingId];
    if (item.documentItem.buildingGroupId) {
      let buildingGroupIds = await this.buildingGroupService
        .getBuildingsByBuildingGroupId(item.documentItem.buildingGroupId)
        .toPromise();
      currentBGBuildings = buildingGroupIds.map(
        (building) => building.buildingId
      );
    }
    const dto = {
      language: item.lang,
      buildingIds: currentBGBuildings,
    };

    this.documentListService
      .printDocument(dto)
      .pipe(first())
      .subscribe(() => {
        dto.buildingIds.forEach((x) =>
          this.documentListService
            .getDocumentByBuildingId(x)
            .pipe(first())
            .subscribe((newDocs) => {
              this.updateItems(newDocs);
              item.callBack();
            })
        );
      });
  }

  onSignedPreview(item: DocumentListItemDTO) {
    this.selectedApprovalObject = item;
    this.state = State.SignedPreview;
  }

  onSignedPreviewClose() {
    this.state = State.List;
    this.ngOnInit();
  }

  get signedApprovalScreenVisible() {
    return this.state === State.SignedPreview;
  }

  onShowRejectedPreview(item: DocumentListItemDTO) {
    this.selectedApprovalObject = item;
    this.state = State.RejectedPreview;
  }

  get rejectedPreviewVisible() {
    return this.state === State.RejectedPreview;
  }

  onRejectedPreviewClose() {
    this.state = State.List;
    this.ngOnInit();
  }

  selectedItemRejectedId() {
    return "selected-rejected-upload";
  }

  canToggleNoAdminState(item: DocumentListItemDTO) {
    if (!this.canWrite) return false;
    if (item.status != this.surveyStatus.InsideDone) return false;
    if (item.insideSurveyId == undefined) return false;
    if (item.tsaType == undefined) return true;
    if (item.tsaType == TSAType.NoAdmin || item.noAdminTSA) return true;
    if (item.tsaType == TSAType.NoTsa || item.noTSA) return true;
    if (item.tsaType == TSAType.Normal || item.tsa) return false;
    return false;
  }

  canToggleNoTsaState(item: DocumentListItemDTO) {
    if (!this.canWrite) return false;
    if (item.status != this.surveyStatus.InsideDone) return false;
    if (item.insideSurveyId == undefined) return false;
    if (item.tsaType == undefined) return true;
    if (item.documentStatus < DocumentState.Printed) return true;
    return false;
  }

  canToggleTsaState(item: DocumentListItemDTO) {
    if (!this.canWrite) return false;
    if (item.status != this.surveyStatus.InsideDone) return false;
    if (
      item.documentStatus == DocumentState.Printed &&
      item.tsaType == TSAType.Normal
    )
      return false;
    if (item.insideSurveyId == undefined) return false;
    return true;
  }

  canTogglePrintState(item: DocumentListItemDTO) {
    if (!this.canWrite) return false;
    if (
      item.status == this.surveyStatus.InsideDone &&
      item.tsaType != TSAType.NoTsa
    )
      return true;
    else return false;
  }

  private resetTsaAndChangeType(item: DocumentListItemDTO, tSAType: TSAType) {
    this.dialogService
      .openDialog({
        headerText: $localize`:@@document.rollback.header: Reset TSA`,
        detailText: $localize`:@@document.rollback.detailtext: Reset will clear TSA status and Print date. Warning: New TSA must be printed!`,
        dialogType: DialogType.Continue,
      })
      .subscribe((feedback) => {
        if (feedback) item.tsaType = tSAType;
        else return;

        this.documentListService
          .backToPending(item.buildingId)
          .pipe(first())
          .subscribe(() => {
            this.setDocumentType(item.insideSurveyId, item.tsaType);
          });
      }),
      () => {
        item.noAdminTSA = !item.noAdminTSA;
      };
  }

  private async setDocumentType(surveyId: string, tsaType: TSAType) {
    return this.documentListService
      .setNoAdminTsa({
        insideSurveyId: surveyId,
        tsaType: tsaType,
      })
      .subscribe((res) => {
        this.getData();
      });
  }

  private getData() {
    if (this.areaId) {
      this.documentListService
        .isDefaultArea(this.areaId)
        .pipe(first())
        .subscribe((res) => (this.isDefaultOwner = res));

      this.documentListService
        .getDocumentListItemsByAreaId(this.areaId)
        .pipe(first())
        .subscribe((res) => {
          this.tableValues = res;
          this._figureService.MapFigureToBuildingGroup(this.tableValues as any);
          this.globalFilter$.next(this.globalFilter);
        });
    } else {
      this.globalFilter$.next(this.globalFilter);
    }
  }
}
